import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useStore } from "../../stores/store";
import IagoAvatar from "./IagoAvatar";

const SESSION_BALLOON_KEY = "onboard.iago.balloonShown";

function shouldShowBalloon(dismissedAt: string | null): boolean {
  if (typeof window === "undefined") return false;
  if (sessionStorage.getItem(SESSION_BALLOON_KEY) === "1") return false;
  if (!dismissedAt) return true;
  const ts = Date.parse(dismissedAt);
  if (Number.isNaN(ts)) return true;
  const hoursSince = (Date.now() - ts) / (1000 * 60 * 60);
  return hoursSince >= 24;
}

export default function IagoFloater() {
  const chatOpen = useStore((s) => s.chatOpen);
  const toggleChat = useStore((s) => s.toggleChat);
  const userSettings = useStore((s) => s.userSettings);
  const dismissIagoBalloon = useStore((s) => s.dismissIagoBalloon);

  const [balloon, setBalloon] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!userSettings.tutorialCompleted) return;
    if (chatOpen) return;
    if (shouldShowBalloon(userSettings.iagoBalloonDismissedAt)) {
      const t = setTimeout(() => setBalloon(true), 700);
      return () => clearTimeout(t);
    }
  }, [userSettings.tutorialCompleted, userSettings.iagoBalloonDismissedAt, chatOpen]);

  function dismiss() {
    setBalloon(false);
    try {
      sessionStorage.setItem(SESSION_BALLOON_KEY, "1");
    } catch {}
    dismissIagoBalloon().catch(() => {});
  }

  function openChat() {
    if (balloon) {
      try {
        sessionStorage.setItem(SESSION_BALLOON_KEY, "1");
      } catch {}
      setBalloon(false);
    }
    toggleChat();
  }

  if (chatOpen || !userSettings.tutorialCompleted) return null;

  const greeting = userSettings.nickname
    ? `Oi, ${userSettings.nickname}. Quando precisar, é só chamar.`
    : "Oi. Quando precisar, é só chamar.";

  return (
    <div className="fixed bottom-5 right-5 z-40 flex items-end gap-3">
      <AnimatePresence>
        {balloon && (
          <motion.div
            key="balloon"
            initial={{ opacity: 0, x: 12, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 12, scale: 0.95 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            className="relative mb-3 max-w-[260px] rounded-2xl rounded-br-sm border border-cyan-400/30 bg-slate-900/95 px-4 py-2.5 text-sm text-slate-100 shadow-[0_10px_30px_rgba(8,145,178,0.35)] backdrop-blur"
          >
            <button
              onClick={dismiss}
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-white/15 bg-slate-800 text-slate-300 hover:text-rose-300"
              aria-label="dispensar"
            >
              <X className="h-3 w-3" />
            </button>
            {greeting}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={openChat}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.96 }}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-slate-900/80 ring-1 ring-cyan-400/40 shadow-[0_0_28px_rgba(34,211,238,0.45)] backdrop-blur hover:ring-cyan-300/70"
        title="abrir chat com IAGO"
        aria-label="Abrir chat com IAGO"
      >
        <IagoAvatar size={48} state={hovered ? "greeting" : "idle"} breathe />
      </motion.button>
    </div>
  );
}
